import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login, selectTenant, clearError } from '../store/slices/authSlice';

function UserLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedPAAS, setSelectedPAAS] = useState('');
  const [selectedSaaS, setSelectedSaaS] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [paasList, setPaasList] = useState([]);
  const [saasList, setSaasList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, user, tenants } = useSelector((state) => state.auth);

  useEffect(() => {
    if (tenants.length > 0) {
      const paasTenants = tenants.filter(t => t.paasId);
      setPaasList(paasTenants);
      setSaasList(tenants.filter(t => t.saasId));
      setClientList(tenants.filter(t => t.tenantId));
    }
  }, [tenants]);

  useEffect(() => {
    if (isAuthenticated && user?.role) {
      const userRole = user.role.toLowerCase();
      if (userRole === 'superadmin') {
        navigate('/super-admin/dashboard');
      } else if (userRole === 'paas-admin' || userRole === 'paas_admin') {
        navigate(`/paas-admin/${selectedPAAS}`);
      } else if (userRole === 'saas-admin' || userRole === 'saas_admin') {
        navigate(`/saas-admin/${selectedSaaS}`);
      } else if (userRole === 'client-admin' || userRole === 'client_admin') {
        navigate(`/client-admin/${selectedClient}`);
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());
    
    const result = await dispatch(login({ email, password }));
    
    if (login.fulfilled.match(result)) {
      const loggedInUser = result.payload.user;
      const userTenants = result.payload.tenants || [];
      const userRole = loggedInUser?.role?.toLowerCase();
      
      if (userRole === 'superadmin') {
        navigate('/super-admin/dashboard');
      } else if (userRole === 'paas-admin' || userRole === 'paas_admin') {
        navigate(`/paas-admin/${selectedPAAS}`);
      } else if (userRole === 'saas-admin' || userRole === 'saas_admin') {
        navigate(`/saas-admin/${selectedSaaS}`);
      } else if (userRole === 'client-admin' || userRole === 'client_admin') {
        navigate(`/client-admin/${selectedClient}`);
      } else if (userTenants.length > 0) {
        const defaultTenant = userTenants[0];
        await dispatch(selectTenant(defaultTenant.tenantId));
        navigate('/dashboard');
      } else {
        navigate('/dashboard');
      }
    }
  };

  return (
    <div className="login-page" style={styles.page}>
      <div className="login-card" style={styles.card}>
        <div style={styles.logoSection}>
          <div style={styles.logo}>👤</div>
          <h1 style={styles.title}>User Login</h1>
          <p style={styles.subtitle}>Sign in to your account</p>
        </div>
        
        {error && <div style={styles.error}>{error}</div>}
        
        <form onSubmit={handleSubmit} style={styles.form}>
          {tenants.length > 1 && (
            <>
              <div className="form-group" style={styles.formGroup}>
                <label style={styles.label}>Select Platform (PAAS)</label>
                <select
                  value={selectedPAAS}
                  onChange={(e) => {
                    setSelectedPAAS(e.target.value);
                    setSelectedSaaS('');
                    setSelectedClient('');
                  }}
                  style={styles.select}
                >
                  <option value="">Select a platform (optional)...</option>
                  {paasList.map(paas => (
                    <option key={paas.paasId} value={paas.paasId}>
                      {paas.name || paas.paasId}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedPAAS && (
                <div className="form-group" style={styles.formGroup}>
                  <label style={styles.label}>Select Application (SAAS)</label>
                  <select
                    value={selectedSaaS}
                    onChange={(e) => {
                      setSelectedSaaS(e.target.value);
                      setSelectedClient('');
                    }}
                    style={styles.select}
                  >
                    <option value="">Select an application (optional)...</option>
                    {saasList
                      .filter(s => !selectedPAAS || s.paasId === selectedPAAS)
                      .map(saas => (
                      <option key={saas.saasId} value={saas.saasId}>
                        {saas.name || saas.saasId}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {selectedSaaS && (
                <div className="form-group" style={styles.formGroup}>
                  <label style={styles.label}>Select Organization</label>
                  <select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">Select an organization (optional)...</option>
                    {clientList
                      .filter(c => !selectedSaaS || c.saasId === selectedSaaS)
                      .map(client => (
                      <option key={client.tenantId} value={client.tenantId}>
                        {client.name || client.tenantId}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
          
          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              style={styles.input}
              required
            />
          </div>
          
          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              style={styles.input}
              required
            />
          </div>
          
          <button 
            type="submit" 
            disabled={isLoading}
            style={styles.button}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        <div style={styles.footer}>
          <a href="/login" style={styles.link}>← Back to Main Login</a>
          <span style={styles.divider}>•</span>
          <a href="/register" style={styles.link}>Create Account</a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)',
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logo: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: '#6b7280',
    fontSize: '14px',
    margin: 0,
  },
  error: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  input: {
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    transition: 'border-color 0.2s',
    outline: 'none',
  },
  select: {
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none',
    backgroundColor: 'white',
  },
  button: {
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  footer: {
    marginTop: '24px',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    alignItems: 'center',
  },
  link: {
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '14px',
  },
  divider: {
    color: '#d1d5db',
  },
};

export default UserLogin;